import { useState } from 'react';
import { Button, TextButton } from '@safetyhub/ui';
import {
  ContactFormFields,
  type ContactFormValue,
} from './ContactFormFields';

interface SiteFormStep3Props {
  contacts: ContactFormValue[];
  onAddContact: (contact: ContactFormValue) => void;
  onRemoveContact: (index: number) => void;
}

const EMPTY_CONTACT: ContactFormValue = {
  name: '',
  phone: '',
  department: '',
  position: '',
  email: '',
};

export function SiteFormStep3({
  contacts,
  onAddContact,
  onRemoveContact,
}: SiteFormStep3Props) {
  const [formValue, setFormValue] = useState<ContactFormValue>(EMPTY_CONTACT);

  const canAdd =
    formValue.name.trim().length > 0 && formValue.phone.trim().length > 0;

  const handleAdd = () => {
    const name = formValue.name.trim();
    const phone = formValue.phone.trim();
    if (!name || !phone) return;
    onAddContact({
      name,
      phone,
      department: formValue.department?.trim() || undefined,
      position: formValue.position?.trim() || undefined,
      email: formValue.email?.trim() || undefined,
    });
    setFormValue(EMPTY_CONTACT);
  };

  return (
    <div className="space-y-5 min-w-0">
      <p className="text-xs text-ink-500">
        현장 담당자를 추가합니다. 건너뛰거나 사업장 상세에서 나중에 입력할 수도
        있습니다.
      </p>

      {/* 추가 폼 */}
      <section className="p-3 border border-line rounded-card min-w-0">
        <h3 className="text-sm font-medium text-ink-900 mb-2">담당자 추가</h3>
        <ContactFormFields value={formValue} onChange={setFormValue} />
        <div className="mt-3 flex items-center justify-end gap-2">
          {/* 비활성 사유를 적는다 — 성명·연락처에 *가 있어도, 부서만 채우고 누른 사람에게는
              "버튼이 먹통"으로 읽힌다(2026-08-11 사용자 제보). */}
          {!canAdd && (
            <span className="text-xs text-ink-400">성명과 연락처를 입력하세요.</span>
          )}
          <Button variant="primary" size="sm" onClick={handleAdd} disabled={!canAdd}>
            담당자 추가
          </Button>
        </div>
      </section>

      {/* 리스트 */}
      <section className="min-w-0">
        <h3 className="text-sm font-medium text-ink-900 mb-2">
          등록된 담당자 ({contacts.length}명)
        </h3>
        {contacts.length === 0 ? (
          <p className="text-xs text-ink-500 text-center py-3">
            아직 등록된 담당자가 없습니다.
          </p>
        ) : (
          <ul className="divide-y divide-line border border-line rounded-card">
            {contacts.map((contact, idx) => (
              <li
                key={`${contact.name}-${contact.phone}-${idx}`}
                className="flex items-center gap-3 py-2 px-3 min-w-0"
              >
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-green-700 font-medium text-xs">
                    {contact.name.charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 min-w-0">
                    <p className="text-sm font-medium text-ink-900 truncate">
                      {contact.name}
                    </p>
                    {contact.position && (
                      <span className="flex-shrink-0 text-xs text-ink-500">
                        ({contact.position})
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-ink-500 truncate">
                    {contact.department && (
                      <span>{contact.department} · </span>
                    )}
                    {contact.phone}
                  </p>
                </div>
                <TextButton
                  tone="danger"
                  onClick={() => onRemoveContact(idx)}
                  className="flex-shrink-0 text-xs"
                >
                  삭제
                </TextButton>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
